const asyncImport = require('util/asyncImport').default;
const {
	OUT_EASING,
	DELAY_DEBOUNCE,
	DIRECTION_LEFT,
	DIRECTION_RIGHT,
} = require('./consts');
const { stuttering, default: onWidthResize } = require('./onWidthResize');
const {
	default: { mediumDelay },
} = require('./sass-variables');
const {
	getNumber,
	getClosest,
	isFocusable,
	getFocusablesFrom,
} = require('./functions');
const isRtl = require('./isRtl').default;

const duration = getNumber(mediumDelay) * 1000;

const CLASS_ACTIVE = 'active';
const CLASS_IS_PANNING = 'is-panning';

// initialize parameters:
//  swipedList
//    list of elements that will be swiped.
//  touchZone
//    element in wich the swiping fill happen
//  swipeContainer
//    the element in wich the div that will be swiped will move according to --offset
//
// in object declarable methods:
//  onResize
//    the resize event
//  onFirst, on last
//    called when on the first or the last element respectively
//  offFirst, offLast
//    called when out of them
//
// import the object in your widget with a Object.assign(this, MiniSwipe);

const toggleSwiped = (swiped, isActive, hasSwipe) => {
	swiped.classList.toggle(CLASS_ACTIVE, isActive);
	if (hasSwipe || hasSwipe == undefined) {
		[
			...getFocusablesFrom(swiped),
			...(isFocusable(swiped) ? [swiped] : []),
		].forEach(element => element.setAttribute('tabindex', isActive ? 0 : -1));
	}
};

module.exports = {
	async initialize({
		swipedList,
		touchZone,
		swipeContainer,
		markTilesActive,
		hasSwipe,
	}) {
		this.swipedList = swipedList;
		this.markTilesActive = markTilesActive;
		this.swipeContainer = swipeContainer;
		this.touchZone = touchZone;
		this.hasSwipe = hasSwipe;
		this.currentOffset = 0;
		this.target = 0;
		this.viewablesCount = 1;
		await asyncImport(this, ['anime', 'Hammer', 'throttle']);

		this.defineNotches();
		this.initTouchEvents();
		this.setTilesActive(0);
	},
	initTouchEvents() {
		this.touch = new this.Hammer(this.touchZone);
		const throtteledActivation = this.throttle(
			this.setTilesActive.bind(this),
			50,
		);
		this.touch.on('pan', e => {
			const offset = this.deltaMove(e.deltaX);
			// to precent link click while panning on desktop
			this.touchZone.classList.add(CLASS_IS_PANNING);
			throtteledActivation(offset);
		});
		this.touch.on('panend', this.panEnd.bind(this));
		if (this.onResize) {
			onWidthResize(
				() => {
					this.onResize();
					if (this.markTilesActive) this.setTilesActive();
				},
				stuttering.DEBOUNCE,
				DELAY_DEBOUNCE,
			);
			this.onResize();
		}
	},
	panEnd(e) {
		this.deltaMove(e.deltaX);
		this.currentOffset = this.getOffsetFromDelta(e.deltaX);
		this.adjustToNotch(this.currentOffset);
		this.touchZone.classList.remove(CLASS_IS_PANNING);
	},
	setOffset(offset) {
		this.swipeContainer.style.setProperty('--offset', offset);
		this.offset = offset;
		this.target = offset;
	},
	deactivate() {
		if (this.touch) this.touch.set({ enable: false });
		this.setOffset(0);
	},
	activate() {
		this.touch.set({ enable: true });
		this.defineNotches();
	},
	defineNotches() {
		const list = [...this.swipedList];
		const getOffset = element => {
			const position =
				element.getBoundingClientRect()[isRtl ? 'right' : 'left'];
			return isRtl ? window.innerWidth - position : position;
		};
		const origin = getOffset(this.swipeContainer);
		this.notches = list.map(swiped => getOffset(swiped) - origin);
		this.lastNotch = this.notches[this.notches.length - this.viewablesCount];
	},
	getOffsetFromDelta(delta) {
		return this.limit(this.currentOffset + delta * (isRtl ? 1 : -1));
	},
	deltaMove(delta) {
		const offset = this.getOffsetFromDelta(delta);
		this.setOffset(-offset);
		return offset;
	},
	next() {
		this.MoveToDirection(true);
	},
	previous() {
		this.MoveToDirection(false);
	},
	MoveToDirection(isNext) {
		const currentIndex = this.notches.indexOf(
			getClosest(this.notches, Math.abs(this.target)),
		);
		const incrementedIndex = currentIndex + (isNext ? 1 : -1);
		this.jumpToIndex(this.getLoopedIndex(incrementedIndex));
	},
	getLoopedIndex(index) {
		const lastIndex = this.notches.indexOf(this.lastNotch) + 1;
		return (index + lastIndex) % lastIndex;
	},
	jumpToIndex(index) {
		this.adjustToNotch(this.notches[this.getLoopedIndex(index)]);
	},
	adjustToNotch(value) {
		if (this.target !== value) {
			const notch = getClosest(this.notches, this.limit(value));
			this.target = notch;
			const animation = {
				offset: -notch,
				duration,
				easing: OUT_EASING,
				update: ({ animations: [{ currentValue }] }) => {
					this.setOffset(currentValue);
				},
			};
			this.anime({
				...animation,
				targets: this,
				currentOffset: [this.currentOffset, notch],
			});
			if (this.markTilesActive) this.setTilesActive();
		}
		this.adjustToNotchEvents();
	},
	getNotchIndex(notchToFind) {
		const notchWidth = Math.abs(this.notches[1] - this.notches[0]);
		const half = notchWidth / 2;
		let index = 0;
		this.notches.some((notch, i) => {
			if (notchToFind <= notch + half) {
				index = i;
				return true;
			}
			return false;
		});
		return index;
	},
	setTilesActive(givenTarget) {
		const target = givenTarget !== undefined ? givenTarget : this.target;
		const currentIndex = this.getNotchIndex(target);
		this.onUpdate && this.onUpdate(currentIndex);
		this.swipedList.forEach((swiped, i) => {
			if (i >= currentIndex && i <= currentIndex + this.viewablesCount - 1)
				toggleSwiped(swiped, true, this.hasSwipe);
			else toggleSwiped(swiped, false, this.hasSwipe);
		});
	},
	adjustToNotchEvents() {
		const current = Math.abs(this.target);
		if (this.onFirst) {
			if (current === 0) this.onFirst();
			else this.offFirst();
		}
		if (this.onLast) {
			if (current === this.lastNotch) this.onLast();
			else this.offLast();
		}
		this.setTilesActive();
	},
	limit(offset) {
		return Math.max(0, Math.min(offset, this.lastNotch));
	},
};
